// app/composables/useCampus.ts
// Logique réactive du carrousel campus (un campus à la fois, avec map individuelle)

import { computed, onMounted, ref, watch } from 'vue'
import type { Ref } from 'vue'
import type { Map as LeafletMap, Marker as LeafletMarker } from 'leaflet'
import type { CampusBlock } from '~/types/school'
import { configureLeafletDefaultIcons } from '~/utils/leafletIcons'
// ⚠️ geocodeAddress et getAddressCoordinates sont auto-importés depuis app/utils/geocoding.ts

export const useCampus = (campusBlockData: Ref<CampusBlock | null>) => {
  // ── State du carrousel ───────────────────────────────────
  const currentCampusIndex = ref(0)

  const currentCampus = computed(() => campusBlockData.value?.Campuses[currentCampusIndex.value])

  // Cache des coordonnées par index de campus
  const geocodedCoords = ref<Record<number, GeoCoords>>({})

  const currentCampusHasMap = computed(
    () => !!currentCampus.value && !!geocodedCoords.value[currentCampusIndex.value],
  )

  // ── Instances Leaflet ────────────────────────────────────
  const mapInstance: Ref<LeafletMap | null> = ref(null)
  const markerInstance: Ref<LeafletMarker | null> = ref(null)

  // ── Init / refresh de la map ─────────────────────────────
  const initializeMap = async (): Promise<void> => {
    if (!currentCampus.value) return

    const index = currentCampusIndex.value
    let coords = geocodedCoords.value[index]

    if (!coords) {
      const resolved = await getAddressCoordinates(currentCampus.value.Address)
      if (resolved) {
        geocodedCoords.value[index] = resolved
        coords = resolved
      }
    }

    if (!coords) {
      mapInstance.value?.remove()
      mapInstance.value = null
      return
    }

    await nextTick()

    const mapElement = document.getElementById(`map-${index}`)
    if (!mapElement) return

    // Détruit la carte précédente
    if (mapInstance.value) {
      try {
        mapInstance.value.remove()
      } catch {
        /* noop */
      }
      mapInstance.value = null
    }

    await loadLeafletStyles()
    const { default: L } = await import('leaflet')
    configureLeafletDefaultIcons(L)

    try {
      const map = L.map(`map-${index}`).setView([coords.lat, coords.lng], 15)

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map)

      const marker = L.marker([coords.lat, coords.lng]).addTo(map)
      marker.bindPopup(currentCampus.value.Name)

      mapInstance.value = map
      markerInstance.value = marker

      setTimeout(() => map.invalidateSize(), 100)
    } catch (error) {
      console.error('[useCampus] Création map échouée :', error)
    }
  }

  // ── Navigation ───────────────────────────────────────────
  const nextCampus = async (): Promise<void> => {
    const maxIndex = (campusBlockData.value?.Campuses.length ?? 1) - 1
    if (currentCampusIndex.value < maxIndex) {
      currentCampusIndex.value++
      await nextTick()
      await initializeMap()
    }
  }

  const previousCampus = async (): Promise<void> => {
    if (currentCampusIndex.value > 0) {
      currentCampusIndex.value--
      await nextTick()
      await initializeMap()
    }
  }

  // ── Pré-chargement des coordonnées ──────────────────────
  onMounted(() => {
    const unwatch = watch(
      () => campusBlockData.value?.Campuses,
      async (campuses) => {
        if (!campuses?.length) return

        for (let i = 0; i < campuses.length; i++) {
          const currentCampus = campuses[i]
          if (currentCampus && currentCampus.Address) {
            const coords = await getAddressCoordinates(currentCampus.Address)
            if (coords) geocodedCoords.value[i] = coords
          }
        }

        await nextTick()
        await initializeMap()

        unwatch() // One-shot
      },
      { immediate: true },
    )
  })

  return {
    currentCampusIndex,
    currentCampus,
    currentCampusHasMap,
    geocodedCoords,
    mapInstance,
    markerInstance,
    nextCampus,
    previousCampus,
  }
}
